import { useState, useCallback } from 'react';

import type { CreateCompanyUserPayload } from '@/interfaces';

export interface UseUserFormProps {
  initialState?: Partial<CreateCompanyUserPayload>;
  onSubmit: (payload: CreateCompanyUserPayload) => Promise<void> | void;
  companyId: number;
}

export const useUserForm = ({ initialState, onSubmit, companyId }: UseUserFormProps) => {
  const defaultInitialState: CreateCompanyUserPayload = {
    name: '',
    email: '',
    role: 'USER',
    companyId,
    plants: [],
  };

  const [form, setForm] = useState<CreateCompanyUserPayload>(() => ({
    ...defaultInitialState,
    ...initialState,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateCompanyUserPayload, string>>>({});

  const updateField = useCallback(
    <K extends keyof CreateCompanyUserPayload>(field: K, value: CreateCompanyUserPayload[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof CreateCompanyUserPayload, string>> = {};

    if (!form.name) {
      newErrors.name = 'Name is required';
    }

    if (!form.email) {
      newErrors.email = 'Email is required';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await onSubmit(form);

    setIsSubmitting(false);
  }, [validateForm, onSubmit, form]);

  return {
    form,
    isSubmitting,
    errors,
    updateField,
    updatePlants: (value: { id: number; value: string; label: string }[]) => updateField('plants', value),
    updateRole: (role: 'MANAGER' | 'USER') => updateField('role', role),
    handleSubmit,
  };
};

export default useUserForm;
